<script setup lang="ts">
import { computed, nextTick, onUnmounted, ref } from "vue";
import { useRoute } from "vue-router";
import { storeToRefs } from "pinia";
import { Check, CircleAlert, CircleCheck, CircleDashed, Copy, Download, ExternalLink } from "@lucide/vue";
import { useToast } from "vue-toastification";
import { isAxiosError } from "axios";
import * as Sentry from "@sentry/vue";

import { assignSpeckleUserToProjects, createSpeckleUser, requestSpecklePasswordReset } from "@/api/services/Speckle";

import { startSpeckleConnect } from "./speckleConnect";
import { SPECKLE_CONNECTOR_SERVER_URL, SPECKLE_DOCS_URL, SPECKLE_RELEASES_URL } from "./speckleLinks";
import { useAuthStateStore } from "@/stores/Utils/auth/AuthState";
import { useSpeckleUser } from "@/stores/speckle/speckleUser.store";

import SpeckleLogo from "@/assets/Logos/speckle.svg";
import Button from "@/components/Reusables/Buttons/Button.vue";
import Divider from "@/components/Reusables/Divider/Divider.vue";
import TextInputNew from "@/components/Reusables/Inputs/TextInputNew.vue";
import LoadingWrapper from "@/components/Reusables/Loading/LoadingWrapper/LoadingWrapper.vue";

// Shared with the project's 3D model section, so both point the connector at the same server and guide.
const speckleServerUrl = SPECKLE_CONNECTOR_SERVER_URL;

const toast = useToast();
const route = useRoute();

// The backend decides the account status: it checks the speckleUserData mapping row
// and asks the Speckle server whether an account already exists for the user's email.
// The status lives in the shared speckleUser store so gated UI elsewhere (e.g. the IFC
// upload menu item) reacts immediately when the account is created or connected here.
const speckleUserStore = useSpeckleUser();
const { refreshUserStatus } = speckleUserStore;
const { userStatus, isLoadingStatus, hasStatusError, isLinked: isConnected, hasUnlinkedAccount } = storeToRefs(speckleUserStore);

// The modal is the management surface — always show fresh truth on open.
void refreshUserStatus();

const { user: fronteggUser } = storeToRefs(useAuthStateStore());

// With no Speckle account yet the backend reports email: null, but a new account is always
// created for the Frontegg login email — show that so the user knows which email it will use.
const accountEmail = computed(() => userStatus.value?.email ?? fronteggUser.value?.email ?? "");

// The server URL is pasted into the Speckle connector, so it's copied rather than opened.
const wasCopied = ref(false);
let wasCopiedResetTimeout: ReturnType<typeof setTimeout> | undefined;

const copyServerUrl = async () => {
  try {
    await navigator.clipboard.writeText(speckleServerUrl);
    wasCopied.value = true;
    clearTimeout(wasCopiedResetTimeout);
    wasCopiedResetTimeout = setTimeout(() => (wasCopied.value = false), 2000);
  } catch {
    // the browser denied clipboard access — the URL stays selectable in the row
    toast.error("Couldn't copy the server URL");
  }
};

onUnmounted(() => clearTimeout(wasCopiedResetTimeout));

// Mirrors the backend's (and Speckle's) minimum — the server remains the authority.
const SPECKLE_MIN_PASSWORD_LENGTH = 8;

const newPassword = ref("");
const confirmPassword = ref("");

const passwordError = computed(() => {
  if (!newPassword.value) return "";
  return newPassword.value.length < SPECKLE_MIN_PASSWORD_LENGTH ? "At least 8 characters" : "";
});

const confirmPasswordError = computed(() => {
  if (!confirmPassword.value) return "";
  return confirmPassword.value === newPassword.value ? "" : "Passwords don't match";
});

const canCreateAccount = computed(
  () => newPassword.value.length >= SPECKLE_MIN_PASSWORD_LENGTH && confirmPassword.value === newPassword.value
);

// Progressive disclosure: the password fields only appear once the user commits to creating
// an account, so the default view stays a single primary action.
const isChoosingPassword = ref(false);

const startChoosingPassword = async () => {
  isChoosingPassword.value = true;
  await nextTick();
  document.getElementById("speckle-new-password")?.focus();
};

const cancelChoosingPassword = () => {
  isChoosingPassword.value = false;
  newPassword.value = "";
  confirmPassword.value = "";
};

const isCreatingAccount = ref(false);
// Which stage of the two-step creation is running — drives the progress checklist in the template.
const creationStage = ref<"account" | "projects" | null>(null);

const createAccount = async () => {
  if (isCreatingAccount.value || !canCreateAccount.value) return;

  isCreatingAccount.value = true;
  creationStage.value = "account";
  try {
    await createSpeckleUser(newPassword.value);
  } catch (createError) {
    // 400/409 carry a human-readable reason (weak password / account already exists) — show it as-is.
    const backendMessage = isAxiosError<{ message?: string }>(createError) ? createError.response?.data?.message : undefined;
    if (!backendMessage) Sentry.captureException(createError);
    toast.error(backendMessage ?? "Couldn't create the Speckle account");
    creationStage.value = null;
    isCreatingAccount.value = false;
    return;
  }

  // The account exists from here on — assignment failures must not read as a failed creation.
  creationStage.value = "projects";
  try {
    const { assigned, total } = await assignSpeckleUserToProjects();
    toast.success(total > 0 ? `Speckle account created — added to ${assigned} of ${total} projects` : "Speckle account created");
  } catch (assignError) {
    Sentry.captureException(assignError);
    toast.success("Speckle account created");
    toast.warning('Couldn\'t add you to your projects — use "Sync projects" to retry');
  }

  isChoosingPassword.value = false;
  newPassword.value = "";
  confirmPassword.value = "";
  // pull the fresh status so the modal flips to the connected state
  await refreshUserStatus();
  creationStage.value = null;
  isCreatingAccount.value = false;
};

const isSyncingProjects = ref(false);

// Idempotent repair action: re-checks project access and invites the Speckle user wherever
// membership is missing. Also heals accounts whose assignment failed during create/connect.
const syncProjects = async () => {
  if (isSyncingProjects.value) return;

  isSyncingProjects.value = true;
  try {
    // Only the assigned count is shown — never "assigned of total", so projects that fail
    // to assign (e.g. broken prod projects) don't surface as a partial-failure ratio.
    const { assigned } = await assignSpeckleUserToProjects();
    toast.success(
      assigned > 0
        ? `Synced — your Speckle account has access to ${assigned} ${assigned === 1 ? "project" : "projects"}`
        : "Synced — no Speckle projects available yet"
    );
  } catch (syncError) {
    Sentry.captureException(syncError);
    toast.error("Couldn't sync your projects");
  } finally {
    isSyncingProjects.value = false;
  }
};

const isResettingPassword = ref(false);

const connectAccount = () => {
  // Full-page redirect to Speckle's consent screen; the flow finishes on /integrations/speckle/callback,
  // which links the account and reopens this modal on the page the user left from.
  const started = startSpeckleConnect(route.fullPath);
  if (!started) {
    toast.error("The Speckle app isn't configured (VITE_SPECKLE_APP_ID)");
  }
};

const resetPassword = async () => {
  const email = userStatus.value?.email;
  if (!email || isResettingPassword.value) return;

  isResettingPassword.value = true;
  try {
    await requestSpecklePasswordReset(email);
    toast.success(`Password reset link sent to ${email}`);
  } catch (requestError) {
    Sentry.captureException(requestError);
    toast.error("Couldn't send the password reset email");
  } finally {
    isResettingPassword.value = false;
  }
};
</script>

<template>
  <div class="gap-px-16 flex h-full flex-col">
    <div class="gap-px-8 flex items-center">
      <img
        :src="SpeckleLogo"
        class="icon-lg shrink-0"
        alt=""
        aria-hidden="true" />
      <h2 class="t-sh3">Speckle Settings</h2>
    </div>

    <p class="t-small text-type-muted">
      Your Real-Time LCA user is linked to its own Speckle account, which you manage from here.
    </p>

    <LoadingWrapper
      :loading="isLoadingStatus"
      :error="hasStatusError">
      <div class="rounded-8 bg-slate-0 gap-px-12 p-px-16 flex flex-col border border-slate-200">
        <div class="gap-px-16 flex items-center justify-between">
          <span class="t-small-bold">Account status</span>

          <span
            v-if="isConnected"
            class="gap-px-6 flex items-center">
            <CircleCheck
              class="icon-sm shrink-0"
              :stroke-width="1.5"
              color="var(--color-app-green-700)"
              fill="var(--color-app-green-100)"
              aria-hidden="true" />
            <span class="t-small-bold text-app-green-700">Connected</span>
          </span>
          <span
            v-else-if="hasUnlinkedAccount"
            class="gap-px-6 flex items-center">
            <CircleAlert
              class="icon-sm shrink-0"
              :stroke-width="1.5"
              color="var(--color-app-yellow-700)"
              fill="var(--color-app-yellow-100)"
              aria-hidden="true" />
            <span class="t-small-bold text-app-yellow-700">Account found — not linked</span>
          </span>
          <span
            v-else
            class="gap-px-6 flex items-center">
            <CircleDashed
              class="icon-sm text-type-muted shrink-0"
              :stroke-width="1.5"
              aria-hidden="true" />
            <span class="t-small-bold text-type-muted">Not connected</span>
          </span>
        </div>

        <Divider spacing="none" />

        <div class="gap-px-8 flex flex-col">
          <div
            v-if="accountEmail"
            class="gap-px-16 flex items-center justify-between">
            <span class="t-small text-type-muted shrink-0">Account</span>
            <span class="t-small min-w-0 truncate">{{ accountEmail }}</span>
          </div>
          <div
            v-if="userStatus?.speckleId"
            class="gap-px-16 flex items-center justify-between">
            <span class="t-small text-type-muted shrink-0">Speckle ID</span>
            <span class="t-small min-w-0 truncate">{{ userStatus.speckleId }}</span>
          </div>
          <div
            v-if="speckleServerUrl"
            class="gap-px-16 flex items-center justify-between">
            <span class="t-small text-type-muted shrink-0">Server</span>
            <div class="gap-px-4 flex min-w-0 items-center">
              <span
                v-tooltip.truncated="speckleServerUrl"
                class="t-small min-w-0 truncate select-all"
                >{{ speckleServerUrl }}</span
              >
              <button
                type="button"
                class="focus-ring interactive-active duration-fast ease-standard rounded-4 p-px-4 shrink-0 transition-colors hover:bg-slate-100"
                :aria-label="wasCopied ? 'Server URL copied' : 'Copy server URL'"
                @click="copyServerUrl">
                <Check
                  v-if="wasCopied"
                  class="icon-sm text-app-green-700 shrink-0"
                  :stroke-width="1.5"
                  aria-hidden="true" />
                <Copy
                  v-else
                  class="icon-sm text-type-muted shrink-0"
                  :stroke-width="1.5"
                  aria-hidden="true" />
              </button>
            </div>
          </div>
        </div>

        <template v-if="isConnected">
          <div class="gap-px-8 pt-px-4 flex">
            <Button
              label="Reset password"
              color="button"
              :is-loading="isResettingPassword"
              @click="resetPassword" />
            <Button
              label="Sync projects"
              color="button"
              :is-loading="isSyncingProjects"
              @click="syncProjects" />
          </div>
          <p class="t-caption text-type-muted">
            You sign in to Speckle with the account email above. Use reset password to set or change it — the link is sent to your
            email. Sync projects re-checks your project access and adds your Speckle user where it's missing.
          </p>
        </template>
        <template v-else-if="hasUnlinkedAccount">
          <p class="t-small">
            A Speckle account for your email already exists on the server, but it isn't linked to your Real-Time LCA user yet. If
            you don't know the account's password, reset it first — then connect the account.
          </p>
          <div class="gap-px-8 pt-px-4 flex">
            <Button
              label="Connect account"
              color="primary"
              @click="connectAccount" />
            <Button
              label="Reset password"
              color="button"
              :is-loading="isResettingPassword"
              @click="resetPassword" />
          </div>
        </template>
        <template v-else>
          <p
            class="t-small"
            aria-live="polite">
            <template v-if="isCreatingAccount">
              {{ creationStage === "account" ? "Creating your Speckle account…" : "Adding you to your projects…" }}
            </template>
            <template v-else-if="!isChoosingPassword">
              No Speckle account is linked to your user yet. Create one to view and share your project models on Speckle.
            </template>
            <template v-else>
              Choose the password for your new Speckle account — you sign in to Speckle with your email and this password.
            </template>
          </p>

          <Transition name="fade-in">
            <div
              v-if="isChoosingPassword"
              class="gap-px-12 flex flex-col"
              @keyup.enter="createAccount">
              <TextInputNew
                id="speckle-new-password"
                v-model="newPassword"
                label="Speckle password"
                type="password"
                :error-msg="passwordError"
                required />
              <TextInputNew
                v-model="confirmPassword"
                label="Confirm password"
                type="password"
                :error-msg="confirmPasswordError"
                required />
            </div>
          </Transition>

          <div class="gap-px-8 pt-px-4 flex">
            <Button
              v-if="!isChoosingPassword"
              label="Create Speckle account"
              color="primary"
              @click="startChoosingPassword" />
            <template v-else>
              <Button
                label="Create account"
                color="primary"
                :is-loading="isCreatingAccount"
                :disabled="!canCreateAccount"
                @click="createAccount" />
              <Button
                label="Cancel"
                color="button"
                :disabled="isCreatingAccount"
                @click="cancelChoosingPassword" />
            </template>
          </div>
        </template>
      </div>

      <div
        v-if="isConnected"
        class="rounded-8 bg-slate-0 gap-px-12 p-px-16 flex flex-col border border-slate-200">
        <div class="gap-px-4 flex flex-col">
          <span class="t-small-bold">Speckle connector</span>
          <p class="t-caption text-type-muted">
            Publish models from Revit, ArchiCAD, Rhino and Grasshopper. Install the connector, then sign in to the server above
            with your Speckle account.
          </p>
        </div>

        <div class="gap-px-12 flex flex-wrap items-center">
          <a
            :href="SPECKLE_RELEASES_URL"
            target="_blank"
            rel="noopener noreferrer"
            class="focus-ring interactive-active duration-fast ease-standard rounded-8 bg-app-blue-600 dark:bg-app-blue-fill hover:bg-app-blue-700 dark:hover:bg-app-blue-fill-hover t-small-bold gap-px-8 flex min-h-[40px] items-center px-5 py-2.5 text-white transition-all">
            <Download
              class="icon-sm shrink-0"
              :stroke-width="1.5"
              aria-hidden="true" />
            <span>Download connector</span>
          </a>

          <a
            :href="SPECKLE_DOCS_URL"
            target="_blank"
            rel="noopener noreferrer"
            class="focus-ring rounded-4 t-caption text-app-blue-700 gap-px-4 flex items-center hover:underline">
            <span>Setup guide per tool</span>
            <ExternalLink
              class="icon-xs shrink-0"
              :stroke-width="1.5"
              aria-hidden="true" />
          </a>
        </div>
      </div>
    </LoadingWrapper>
  </div>
</template>
